import { useCallback, useEffect, useState } from "react";
import { supportService } from "../../../../services/support-service";
import { AdminPageHeader, AdminPanel, EmptyState, StatusBadge, formatDate } from "./admin-ui";

const statusOptions = ["open", "in_progress", "waiting_user", "resolved", "closed"];
const priorityOptions = ["low", "normal", "high", "urgent"];

function AdminSupportTicketsPage() {
    const [tickets, setTickets] = useState([]);
    const [selected, setSelected] = useState(null);
    const [filter, setFilter] = useState("");
    const [reply, setReply] = useState("");
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(true);

    const load = useCallback(() => {
        setLoading(true);
        supportService.adminTickets({ status: filter })
            .then((data) => {
                setTickets(data.tickets || []);
                setSelected((current) => current ? (data.tickets || []).find((ticket) => ticket.id === current.id) || null : null);
            })
            .catch((error) => setMessage(error.message))
            .finally(() => setLoading(false));
    }, [filter]);

    useEffect(() => {
        load();
    }, [load]);

    const updateTicket = async (payload) => {
        if (!selected) return;
        try {
            const data = await supportService.adminUpdateTicket(selected.id, payload);
            setSelected(data.ticket || null);
            setReply("");
            setMessage("Support ticket updated.");
            load();
        } catch (error) {
            setMessage(error.message);
        }
    };

    return (
        <>
            <AdminPageHeader title="Support Tickets" subtitle="Reply to candidate and employer support requests" />
            {message && <div className="alert alert-info">{message}</div>}
            <div className="row">
                <div className="col-xl-7 col-lg-12 mb-4">
                    <AdminPanel
                        title="Ticket Queue"
                        icon="fa fa-headset"
                        right={(
                            <select className="form-control" style={{ width: 180 }} value={filter} onChange={(event) => setFilter(event.target.value)}>
                                <option value="">All Statuses</option>
                                {statusOptions.map((status) => <option key={status} value={status}>{status.replace(/_/g, " ")}</option>)}
                            </select>
                        )}
                    >
                        <div className="table-responsive">
                            <table className="table table-bordered">
                                <thead><tr><th>Subject</th><th>User</th><th>Status</th><th>Priority</th><th>Updated</th><th>Action</th></tr></thead>
                                <tbody>
                                    {loading ? (
                                        <tr><td colSpan="6"><EmptyState text="Syncing support tickets..." /></td></tr>
                                    ) : tickets.length === 0 ? (
                                        <tr><td colSpan="6"><EmptyState text="No support tickets found." /></td></tr>
                                    ) : tickets.map((ticket) => (
                                        <tr key={ticket.id}>
                                            <td>{ticket.subject}<div className="text-muted">{ticket.category}</div></td>
                                            <td>{ticket.user_name}<div className="text-muted">{ticket.user_email}</div></td>
                                            <td><StatusBadge status={ticket.status} /></td>
                                            <td>{ticket.priority}</td>
                                            <td>{formatDate(ticket.updated_at || ticket.created_at)}</td>
                                            <td><button className="btn btn-sm btn-outline-primary" type="button" onClick={() => setSelected(ticket)}>Open</button></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </AdminPanel>
                </div>
                <div className="col-xl-5 col-lg-12 mb-4">
                    <AdminPanel title="Ticket Details" icon="fa fa-comments">
                        {!selected ? (
                            <EmptyState text="Select a ticket to reply or change status." />
                        ) : (
                            <>
                                <h4>{selected.subject}</h4>
                                <p className="text-muted">{selected.user_role} | {selected.user_name} | {selected.user_email}</p>
                                <div className="row">
                                    <div className="col-md-6 m-b15">
                                        <label>Status</label>
                                        <select className="form-control" value={selected.status} onChange={(event) => setSelected({ ...selected, status: event.target.value })}>
                                            {statusOptions.map((status) => <option key={status} value={status}>{status.replace(/_/g, " ")}</option>)}
                                        </select>
                                    </div>
                                    <div className="col-md-6 m-b15">
                                        <label>Priority</label>
                                        <select className="form-control" value={selected.priority} onChange={(event) => setSelected({ ...selected, priority: event.target.value })}>
                                            {priorityOptions.map((priority) => <option key={priority} value={priority}>{priority}</option>)}
                                        </select>
                                    </div>
                                </div>
                                <div className="m-b20">
                                    {(selected.messages || []).map((item) => (
                                        <div key={item.id} className={`p-a15 m-b10 ${item.sender_role === "admin" ? "site-bg-gray" : "bg-light"}`}>
                                            <strong>{item.sender_role === "admin" ? "Admin" : selected.user_name}</strong>
                                            <span className="text-muted ms-2">{formatDate(item.created_at)}</span>
                                            <div>{item.message}</div>
                                        </div>
                                    ))}
                                </div>
                                <label>Admin Reply</label>
                                <textarea className="form-control m-b15" rows={4} value={reply} onChange={(event) => setReply(event.target.value)} />
                                <div className="d-flex gap-2 flex-wrap">
                                    <button className="site-button" type="button" onClick={() => updateTicket({ status: selected.status, priority: selected.priority, reply })}>Save Reply</button>
                                    <button className="site-button-secondry" type="button" onClick={() => updateTicket({ status: "resolved", priority: selected.priority, reply })}>Resolve</button>
                                    <button className="btn btn-outline-dark" type="button" onClick={() => updateTicket({ status: selected.status, priority: selected.priority })}>Save Status</button>
                                </div>
                            </>
                        )}
                    </AdminPanel>
                </div>
            </div>
        </>
    );
}

export default AdminSupportTicketsPage;
